import { SignalingCore } from "./core";
import type { JoinOptions, SignalingAdapter, SignalPayload, WireMessage } from "./types";

type Deliver = (message: WireMessage) => void;

/** Carries messages between memory adapters: in-process, or across tabs via BroadcastChannel. */
export interface MemoryBus {
  subscribe(topic: string, deliver: Deliver): () => void;
  publish(topic: string, message: WireMessage): void;
}

/** A single in-process hub (unit tests). Delivery is async, like a real network. */
export function createMemoryBus(): MemoryBus {
  const topics = new Map<string, Set<Deliver>>();
  return {
    subscribe(topic, deliver) {
      const subscribers = topics.get(topic) ?? new Set();
      topics.set(topic, subscribers);
      subscribers.add(deliver);
      return () => subscribers.delete(deliver);
    },
    publish(topic, message) {
      for (const deliver of topics.get(topic) ?? []) queueMicrotask(() => deliver(message));
    },
  };
}

/** Same-origin tabs of one browser profile (Playwright pages sharing a context). */
export function createBroadcastChannelBus(name: string): MemoryBus {
  const channel = new BroadcastChannel(`codeshare-signal:${name}`);
  const local = createMemoryBus();
  channel.onmessage = (event: MessageEvent<{ topic: string; message: WireMessage }>) => {
    local.publish(event.data.topic, event.data.message);
  };
  return {
    subscribe: local.subscribe,
    publish(topic, message) {
      local.publish(topic, message); // other adapters in this tab
      channel.postMessage({ topic, message }); // other tabs
    },
  };
}

/** Test-only signaling (docs/PRD.md §2): never offered to users. */
export class MemorySignaling implements SignalingAdapter {
  readonly kind = "memory";
  readonly #bus: MemoryBus;
  readonly #core: SignalingCore;
  #unsubscribe: (() => void) | undefined;

  constructor(bus: MemoryBus) {
    this.#bus = bus;
    this.#core = new SignalingCore(async (message) => {
      const topic = this.#core.joined?.topic;
      if (topic) this.#bus.publish(topic, message);
    });
  }

  async join(options: JoinOptions): Promise<void> {
    await this.leave();
    this.#core.start(options);
    this.#unsubscribe = this.#bus.subscribe(
      options.topic,
      (message) => void this.#core.receive(message),
    );
    await this.#core.announce();
  }

  onPeerSeen(callback: (peerId: string) => void) {
    return this.#core.onPeerSeen(callback);
  }

  onSignal(callback: (from: string, payload: SignalPayload) => void) {
    return this.#core.onSignal(callback);
  }

  send(to: string, payload: SignalPayload): Promise<void> {
    return this.#core.send(to, payload);
  }

  forget(peerId: string): void {
    this.#core.forget(peerId);
  }

  async leave(): Promise<void> {
    this.#unsubscribe?.();
    this.#unsubscribe = undefined;
    this.#core.stop();
  }
}
